"use client";

import { useEffect, useRef, useState } from "react";

// Visual match for the Flodesk forms on aluume.com (captured 2026-09-30): serif heading
// with one italic phrase, Inconsolata body, underline-only fields, outlined uppercase
// button; fields sit in a row on desktop and stack on mobile. Colours per theme.
// Light sits transparent on the page's cream; dark paints its own background (an
// iframe's canvas isn't reliably see-through across browsers — light text on a light
// canvas would vanish). #393331 = the Thermae section measured on aluume.com.
const THEMES = {
  light: { ink: "#000000", body: "#393331", line: "#393331", button: "#393331", bg: "transparent" },
  dark: { ink: "#F7F2EA", body: "#F7F2EA", line: "#F7F2EA", button: "#F7F2EA", bg: "#393331" },
} as const;

/** "Stay in *touch*" → Stay in <em>touch</em> (plain text only — no HTML from the URL). */
function Heading({ text }: { text: string }) {
  return (
    <>
      {text.split(/(\*[^*]+\*)/g).map((part, i) =>
        part.startsWith("*") && part.endsWith("*") && part.length > 2
          ? <em key={i}>{part.slice(1, -1)}</em>
          : <span key={i}>{part}</span>,
      )}
    </>
  );
}

// Used two ways: inside the /embed/signup iframe on aluume.com (embedded: paints the
// iframe canvas + reports its height to the parent), and inline in the booking site's
// own footer (not embedded: leaves the page alone).
export function SignupForm(props: { theme: "light" | "dark"; bg?: string; tags: string; heading: string; subtitle: string; button: string; thanks: string; embedded?: boolean }) {
  const c = THEMES[props.theme];
  const bg = props.bg ?? c.bg;
  const embedded = props.embedded ?? false;

  // Paint the whole iframe canvas, not just the form box.
  useEffect(() => {
    if (!embedded) return;
    for (const el of [document.documentElement, document.body]) el.style.setProperty("background", bg, "important");
  }, [bg, embedded]);
  const [first, setFirst] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState(""); // honeypot
  const [state, setState] = useState<"idle" | "busy" | "done">("idle");
  const [error, setError] = useState("");
  const root = useRef<HTMLDivElement>(null);

  // Tell the Squarespace page how tall we are, so the iframe never scrolls or clips.
  useEffect(() => {
    const el = root.current;
    if (!el || !embedded) return;
    const post = () => {
      const msg = { type: "aluume-signup-height", height: Math.ceil(el.getBoundingClientRect().height) };
      if (window.parent && window.parent !== window) window.parent.postMessage(msg, "*");
    };
    post();
    const ro = new ResizeObserver(post);
    ro.observe(el);
    return () => ro.disconnect();
  }, [embedded]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setState("busy");
    try {
      const res = await fetch("/api/newsletter", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, first_name: first, tags: props.tags, company }),
      });
      if (res.ok) { setState("done"); return; }
      const j = await res.json().catch(() => ({}));
      setError(j.error === "invalid_email" ? (j.message ?? "Please check your email address.") : "Something went wrong — please try again.");
    } catch {
      setError("Something went wrong — please try again.");
    }
    setState("idle");
  }

  const field = "w-full bg-transparent px-5 py-3 text-[13px] tracking-[0.1px] outline-none focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-current placeholder:text-current placeholder:opacity-100";

  return (
    <div ref={root} className="mx-auto flex max-w-[1200px] flex-col items-center px-4 py-6 text-center" style={{ color: c.body, background: bg, fontFamily: "var(--font-sans)" }}>
      <h2 className="text-[28px] font-light leading-tight" style={{ fontFamily: "var(--font-display)", color: c.ink }}>
        <Heading text={props.heading} />
      </h2>
      {state === "done" ? (
        <p className="mt-6 text-[16px] tracking-[0.3px]" role="status">{props.thanks}</p>
      ) : (
        <>
          <p className="mt-6 max-w-[720px] text-[16px] leading-[1.7] tracking-[0.3px]">{props.subtitle}</p>
          <form onSubmit={submit} className="mt-7 flex w-full flex-col items-stretch gap-3 sm:w-auto sm:flex-row sm:items-end sm:gap-[10px]">
            <label className="sm:w-[193px]">
              <span className="sr-only">First name</span>
              <input value={first} onChange={(e) => setFirst(e.target.value)} placeholder="First name" autoComplete="given-name"
                className={field} style={{ borderBottom: `1px solid ${c.line}`, color: c.ink }} />
            </label>
            <label className="sm:w-[193px]">
              <span className="sr-only">Email address</span>
              <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email address" type="email" required autoComplete="email"
                className={field} style={{ borderBottom: `1px solid ${c.line}`, color: c.ink }} />
            </label>
            <input value={company} onChange={(e) => setCompany(e.target.value)} name="company" tabIndex={-1} autoComplete="off" aria-hidden="true"
              className="absolute -left-[9999px] h-0 w-0 opacity-0" />
            <button type="submit" disabled={state === "busy"}
              className="mt-3 h-[46px] whitespace-nowrap px-5 text-[13px] uppercase tracking-[1.3px] transition-opacity disabled:opacity-50 sm:mt-0"
              style={{ border: `1px solid ${c.button}`, color: c.button, background: "transparent" }}>
              {state === "busy" ? "…" : props.button}
            </button>
          </form>
          {error && <p className="mt-3 text-[13px]" role="alert">{error}</p>}
        </>
      )}
    </div>
  );
}
